/**
 * Product Confirmation Module - Handles the product confirmation popup
 */
import { toastify } from '../helpers/toastify.js';
import { log } from '../utils/log.js';

/**
 * DOM element references
 */
let modal = null;
let productRefEl = null;
let productDescEl = null;
let prixBrutEl = null;
let remiseEl = null;
let prixNetEl = null;
let quantityInput = null;
let totalEl = null;
let confirmBtn = null;
let cancelBtn = null;
let qtyDecreaseBtn = null;
let qtyIncreaseBtn = null;

/**
 * Current product data and callbacks
 */
let currentProduct = null;
let onConfirmCallback = null;
let onCancelCallback = null;

/**
 * Initializes the product confirmation module
 */
export function initProductConfirm() {
  modal = document.getElementById('product-confirm-modal');
  productRefEl = document.getElementById('confirm-product-ref');
  productDescEl = document.getElementById('confirm-product-desc');
  prixBrutEl = document.getElementById('confirm-prix-brut');
  remiseEl = document.getElementById('confirm-remise');
  prixNetEl = document.getElementById('confirm-prix-net');
  quantityInput = document.getElementById('confirm-quantity');
  totalEl = document.getElementById('confirm-total');
  confirmBtn = document.getElementById('confirm-add-btn');
  cancelBtn = document.getElementById('confirm-cancel-btn');
  qtyDecreaseBtn = document.getElementById('qty-decrease');
  qtyIncreaseBtn = document.getElementById('qty-increase');

  // Set up event listeners
  confirmBtn?.addEventListener('click', handleConfirm);
  cancelBtn?.addEventListener('click', handleCancel);
  qtyDecreaseBtn?.addEventListener('click', decreaseQuantity);
  qtyIncreaseBtn?.addEventListener('click', increaseQuantity);
  quantityInput?.addEventListener('input', updateTotal);
  quantityInput?.addEventListener('change', validateQuantity);

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && !modal.hidden) {
      handleCancel();
    }
  });

  // Close on overlay click
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      handleCancel();
    }
  });

  log.info('Product confirmation module initialized');
}

/**
 * Shows the product confirmation modal
 * @param {object} product - Product data from API
 * @param {Function} onConfirm - Callback when user confirms (receives product and quantity)
 * @param {Function} onCancel - Callback when user cancels
 */
export function showProductConfirmation(product, onConfirm, onCancel) {
  if (!modal) {
    log.error('Product confirmation modal not initialized');
    return;
  }

  currentProduct = product;
  onConfirmCallback = onConfirm;
  onCancelCallback = onCancel;

  // Populate modal with product data
  const prixHt = parseFloat(product.option_prix_ht || 0);
  const remise = parseFloat(product.option_remise_conseillee || 0);
  const prixNet = prixHt * (1 - remise / 100);

  productRefEl.textContent = `${product.option_reference || ''} [${product.produit_id || product.option_id}]`;
  productDescEl.textContent = product.option_description || '';
  prixBrutEl.textContent = prixHt.toFixed(2);
  remiseEl.textContent = remise.toFixed(0);
  prixNetEl.textContent = prixNet.toFixed(2);

  // Reset quantity to 1
  quantityInput.value = 1;
  updateTotal();

  // Show modal
  modal.hidden = false;

  // Focus quantity input for easy adjustment
  setTimeout(() => quantityInput?.focus(), 100);

  log.info('Product confirmation modal shown for:', product.option_reference);
}

/**
 * Hides the product confirmation modal
 */
export function hideProductConfirmation() {
  if (modal) {
    modal.hidden = true;
  }
  currentProduct = null;
  onConfirmCallback = null;
  onCancelCallback = null;
}

/**
 * Handles confirm button click
 */
async function handleConfirm() {
  if (!currentProduct || !onConfirmCallback) return;

  const quantity = parseInt(quantityInput.value, 10) || 1;

  // Disable button while processing
  confirmBtn.disabled = true;
  confirmBtn.textContent = 'Ajout...';

  try {
    await onConfirmCallback(currentProduct, quantity);

    // Show success toast
    toastify('Produit ajouté avec succès!', {
      variant: 'success',
      duration: 3000
    });

    hideProductConfirmation();
  } catch (error) {
    log.error('Error adding product:', error);
    toastify('Erreur lors de l\'ajout du produit', {
      variant: 'danger',
      duration: 5000
    });
  } finally {
    confirmBtn.disabled = false;
    confirmBtn.textContent = 'Valider';
  }
}

/**
 * Handles cancel button click
 */
function handleCancel() {
  if (onCancelCallback) {
    onCancelCallback();
  }
  hideProductConfirmation();
}

/**
 * Decreases quantity by 1
 */
function decreaseQuantity() {
  const current = parseInt(quantityInput.value, 10) || 1;
  if (current > 1) {
    quantityInput.value = current - 1;
    updateTotal();
  }
}

/**
 * Increases quantity by 1
 */
function increaseQuantity() {
  const current = parseInt(quantityInput.value, 10) || 1;
  quantityInput.value = current + 1;
  updateTotal();
}

/**
 * Validates quantity input
 */
function validateQuantity() {
  const value = parseInt(quantityInput.value, 10);
  if (isNaN(value) || value < 1) {
    quantityInput.value = 1;
  }
  updateTotal();
}

/**
 * Updates the total display based on quantity
 */
function updateTotal() {
  if (!currentProduct) return;

  const prixHt = parseFloat(currentProduct.option_prix_ht || 0);
  const remise = parseFloat(currentProduct.option_remise_conseillee || 0);
  const prixNet = prixHt * (1 - remise / 100);
  const quantity = parseInt(quantityInput.value, 10) || 1;
  const total = prixNet * quantity;

  totalEl.textContent = total.toFixed(2) + ' €';
}
